// Saved screens (Phase 6 Step 3 — the final screen-as-data step). A localStorage library of named screens
// (the Step-2 {v,f,p,c,s} screen object). Shell-side UI feature: uses localStorage + document directly (like
// portfolio.js / hub.js) → intentionally NOT browser-global-free. Screen read/apply are dependency-injected
// from the app.js shell (readScreenFromUi / applyScreenToDom).
//
// DURABLE-DATA RULES (this is the first persistent user-data feature):
//  • entry.screen is IMMUTABLE once saved — stored VERBATIM. sanitizeScreen() runs ONLY at load-apply time
//    (transient, for the DOM) and is NEVER persisted, so a fk a future registry removes is dropped for this
//    apply but survives in storage for a future app version to re-recognize.
//  • rename touches ONLY the name; remove drops by id. Every persist writes the full verbatim array, so a
//    future/unreadable sibling entry survives.
//  • Version-skew degrades gracefully (drop unknown fks, skip un-loadable screens, keep the rest, never throw)
//    — mirroring how a mangled ?s= loads at defaults.
import { SCR_FILTER_CATALOG } from './screener-filters.js';
import { SCREEN_URL_VERSION } from './screen/url-codec.js';

const KEY = 'abSavedScreensV1';
const STORE_VERSION = 1;
// RS-cohort keys the current app understands (rsCohortPredicate, app.js). Unknown keys are dropped on apply.
const KNOWN_COHORTS = new Set(['delta', 'rsnh', 'rsnh_all', 'primed', 'building', 'firm', 'lagging', 'leaders', 'alpha', 'beta', 'freshDelta', 'sector']);

let _deps = {};
export function configure(d) { _deps = { ..._deps, ...d }; }

export const SavedScreens = (() => {
    const store = {
        read(key, fallback) {
            try { const raw = localStorage.getItem(key); if (!raw) return fallback; const p = JSON.parse(raw); return p ?? fallback; }
            catch (e) { return fallback; }
        },
        write(key, value) {
            try { localStorage.setItem(key, JSON.stringify(value)); return true; }
            catch (e) { console.warn('SavedScreens storage write failed:', e); return false; }
        }
    };

    let screens = null;   // in-memory VERBATIM entries (incl. future/unknown); null = not yet loaded
    let _idSeq = 0;

    function escapeHtml(s) { return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }

    // Read the store honoring the envelope version. Keep EVERY well-formed entry verbatim (future/unknown
    // inner versions included) so a later write can't lose them. Never throws.
    function loadStore() {
        const raw = store.read(KEY, null);
        if (!raw || typeof raw !== 'object' || !Array.isArray(raw.screens)) return [];
        // Envelope version: older → migrate (no migrations at v1; the seam exists for a future bump). Newer →
        // read leniently, don't clobber (we only persist on an explicit save/rename/remove). Either way, keep
        // structurally-valid entries verbatim.
        return raw.screens.filter(e => e && typeof e === 'object' && e.id && e.screen && typeof e.screen === 'object');
    }
    function ensureLoaded() { if (screens === null) screens = loadStore(); return screens; }
    function persist() { store.write(KEY, { v: STORE_VERSION, savedAt: Date.now(), screens }); }

    // Loadable only if the current app understands the inner screen version.
    function loadable(entry) { return !!(entry && entry.screen && entry.screen.v === SCREEN_URL_VERSION); }

    // TRANSIENT sanitize for apply — NEVER persisted. Drops fks/cohort the current app no longer knows.
    function sanitizeScreen(screen) {
        const known = new Set(SCR_FILTER_CATALOG.map(c => c.fk));
        const f = {};
        for (const fk of Object.keys(screen.f || {})) if (known.has(fk)) f[fk] = screen.f[fk];
        const out = { v: screen.v, f };
        if (screen.p) out.p = screen.p;
        if (screen.c && KNOWN_COHORTS.has(screen.c.k)) out.c = { k: screen.c.k, s: screen.c.s || null };
        if (Array.isArray(screen.s)) out.s = screen.s.slice();
        return out;
    }

    function genId() {
        let id;
        do { id = 'scr-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8) + '-' + (_idSeq++); }
        while (ensureLoaded().some(e => e.id === id));
        return id;
    }

    // ── CRUD (store stays verbatim; sanitize only at apply) ──
    function list() {
        return ensureLoaded().map(e => ({ id: e.id, name: e.name, savedAt: e.savedAt, loadable: loadable(e) }));
    }
    function save(name) {
        ensureLoaded();
        const screen = _deps.getCurrentScreen ? _deps.getCurrentScreen() : null;
        if (!screen) return null;
        const entry = { id: genId(), name: String(name || 'Untitled').slice(0, 80), screen, savedAt: Date.now() };
        screens.push(entry); persist(); return entry.id;
    }
    function load(id) {
        const e = ensureLoaded().find(x => x.id === id);
        if (!e || !loadable(e)) return false;
        if (_deps.applyScreen) _deps.applyScreen(sanitizeScreen(e.screen));   // transient sanitize; e.screen untouched
        return true;
    }
    function remove(id) { screens = ensureLoaded().filter(e => e.id !== id); persist(); }
    function rename(id, name) {
        const e = ensureLoaded().find(x => x.id === id);
        if (e) { e.name = String(name || e.name).slice(0, 80); persist(); }   // name ONLY — e.screen verbatim
    }

    // ── UI: saved screens render as CHIPS in the screener "preset" row (#screenerPresetChips).
    // Click a chip = load that screen (like a preset); ✕ = delete; double-click = rename; the trailing
    // "+ Save" chip saves the current screen. Deferred wiring → boot-parity-safe.
    const CHIP = 'scr-saved-chip inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[10px] font-bold transition-colors';
    function render() {
        const host = document.getElementById('screenerPresetChips');
        if (!host) return;
        const chips = list().map(it => `
            <span class="${CHIP} border-slate-700 bg-slate-900 text-slate-300 hover:border-slate-600 hover:text-slate-100 ${it.loadable ? 'cursor-pointer' : 'opacity-50'}" data-id="${escapeHtml(it.id)}" title="${it.loadable ? 'Load this screen · double-click to rename' : 'Saved by a newer version — cannot load'}">
                <span class="scr-saved-name truncate max-w-[150px]">${escapeHtml(it.name)}</span>
                <button type="button" class="scr-saved-del text-slate-500 hover:text-rose-300 leading-none" title="Delete">✕</button>
            </span>`).join('');
        const saveChip = `<button type="button" id="scrSaveChip" class="${CHIP} border-dashed border-slate-600 bg-transparent text-blue-300 hover:text-blue-200 hover:border-blue-500/60" title="Save the current filters, preset, cohort & sort as a named screen">+ Save</button>`;
        host.innerHTML = chips + saveChip;
    }
    function clearActive() {
        const host = document.getElementById('screenerPresetChips');
        if (host) host.querySelectorAll('.scr-saved-chip').forEach(c => c.classList.remove('scr-chip-active'));
    }
    function init() {
        ensureLoaded();
        render();
        const host = document.getElementById('screenerPresetChips');
        if (host && !host._savedWired) {
            host._savedWired = true;
            host.addEventListener('click', (e) => {
                if (e.target.closest('#scrSaveChip')) {
                    const name = window.prompt('Name this screen:');
                    if (name && name.trim()) { save(name.trim()); render(); }
                    return;
                }
                const chip = e.target.closest('[data-id]');
                if (!chip) return;
                const id = chip.dataset.id;
                if (e.target.closest('.scr-saved-del')) { remove(id); render(); return; }
                if (load(id)) { clearActive(); chip.classList.add('scr-chip-active'); }   // highlight the loaded chip
            });
            host.addEventListener('dblclick', (e) => {
                const chip = e.target.closest('[data-id]'); if (!chip) return;
                const cur = list().find(x => x.id === chip.dataset.id);
                const nn = window.prompt('Rename screen:', cur ? cur.name : '');
                if (nn != null && nn.trim()) { rename(chip.dataset.id, nn.trim()); render(); }
            });
        }
    }

    return { init, render, clearActive, list, save, load, remove, rename, _sanitize: sanitizeScreen, _reload: () => { screens = null; } };
})();

if (typeof window !== 'undefined') {
    window.SavedScreens = SavedScreens;
    window.savedScreens = { configure };   // DI seam (tests inject getCurrentScreen/applyScreen spies)
}
